import React from "react";
import { useParams } from "react-router-dom";
import "../../../Styles/PolicyPage.css";

const POLICY_CONTENT = {
  delivery: {
    title: "Delivery Policy",
    content: [
      {
        q: "Standard Delivery (The 24/48 Promise)",
        a: "Elumia guarantees delivery within 24 to 48 hours for all 'In-Stock' items across the UAE.",
      },
      {
        q: "Order Cut-off Time",
        a: "Orders placed before 2:00 PM (GST) are processed for next-day delivery.",
      },
      {
        q: "Temperature-Controlled Shipping",
        a: "For sensitive clinical products such as toxins and dermal fillers, Elumia uses cold-chain logistics to maintain product integrity from warehouse to clinic.",
      },
      {
        q: "Proof of Delivery (POD)",
        a: "All deliveries require a digital signature from a licensed DHA/MOH medical professional or an authorized clinic manager to confirm receipt.",
      },
    ],
  },

  returns: {
    title: "Returns & Refunds Policy",
    content: [
      {
        q: "Return Policy (Unused & Sealed)",
        a: "Customers may initiate a return for unused products within 5 working days of receipt. Products must be in their original, unopened, and undamaged medical-grade packaging, with sterile seals intact.",
      },
      {
        q: "Return Pickup Process",
        a: "Once a return request is approved via the portal, Elumia will coordinate the physical pickup within 2 working days.",
      },
      {
        q: "Return Exclusions",
        a: "Due to hygiene and health safety regulations (MOHAP), items that have been opened, unsealed, or require specific temperature storage that was interrupted by the buyer are strictly non-returnable.",
      },
      {
        q: "Refund Policy (Financial Transparency)",
        a: "Approved refunds will be processed back to the original bank account or payment source used for the purchase.",
      },
      {
        q: "Refund Timeline",
        a: "Please allow 7 to 10 working days for the refund to reflect in your account depending on your banking institution’s processing time.",
      },
      {
        q: "Refund Deductions",
        a: "Delivery charges may be non-refundable unless the return is due to an Elumia error such as an incorrect item being delivered.",
      },
    ],
  },

  replacement: {
    title: "Replacement Policy (QA-Driven)",
    content: [
      {
        q: "Scope (Product-to-Product Replacement)",
        a: "Replacement is only applicable for the same brand and same company, requiring an exact SKU match with the originally purchased product.",
      },
      {
        q: "QA Inspection Requirement",
        a: "All replacement requests are subject to a mandatory Quality Assurance (QA) inspection by Elumia’s compliance team. This includes verification of batch numbers, storage conditions, and possible manufacturing defects.",
      },
      {
        q: "Replacement Timeline",
        a: "Once the product passes QA inspection and replacement is approved, the replacement item will be delivered within 3 to 5 working days.",
      },
      {
        q: "Defective Product Reporting",
        a: "If a product is found to be defective upon delivery, it must be reported within 24 hours to qualify for an immediate prioritized replacement.",
      },
    ],
  },

  insurance: {
    title: "Insurance & Warranty Policy",
    content: [
      {
        q: "Warranty Coverage",
        a: "Elumia acts as the facilitator for manufacturer equipment warranties for medical devices and equipment purchased through our platform.",
      },
      {
        q: "Warranty & Insurance Claims",
        a: "All insurance and technical warranty claims are governed by the original manufacturer’s warranty terms and conditions.",
      },
      {
        q: "Elumia’s Support Role",
        a: "Elumia provides the required documentation such as warranty cards and invoices, and connects the purchasing clinic with the manufacturer’s authorized service center in the UAE for repairs, maintenance, or warranty claims.",
      },
    ],
  },
  payment: {
    title: "Payment Gateway Policy",
    content: [
      {
        q: "Supported Payment Channels",
        a: "Elumia integrates multiple secure payment gateways to ensure smooth checkout and reliable financial processing.",
      },
      {
        q: "NOMAD Integration",
        a: "NOMAD is used for specialized regional marketplace payment processing.",
      },
      {
        q: "Stripe Integration",
        a: "Stripe is used for international and local credit and debit card payments with advanced fraud protection.",
      },
      {
        q: "Mashreq Bank Integration",
        a: "Mashreq Bank integration supports POS transactions and direct bank-linked transfers for clinics and healthcare providers.",
      },
    ],
  },

  terms: {
    title: "Terms of Use",
    content: [
      "TERMS OF USE",
      "Effective Date: [March 9, 2026]",
      'Platform: www.elumiamedstore.com (the "Site")',
      'Owner: Elumia Med Store ("we," "us," or "our")',

      {
        q: "1. Acceptance of Terms",
        a: "By accessing or using Elumia Med Store, you agree to be bound by these Terms of Use and our Privacy Policy. If you do not agree, you must immediately cease using the platform.",
      },
      {
        q: "2. Professional Use Only & Eligibility",
        a: 'Medical Professional Warranty: The Site is a specialized B2B and B2C marketplace for medical products. By purchasing "professional-only" equipment (e.g., emergency crash carts, surgical devices, or restricted drugs), you warrant that you are a licensed healthcare professional or an authorized representative of a licensed medical facility (clinic/hospital) in the UAE. Verification: We reserve the right to request proof of DHA/MOHAP licensing before fulfilling orders for restricted medical items.',
      },
      {
        q: "3. Medical Disclaimer",
        a: "Informational Purposes: All content on the Site, including product descriptions and technical data, is for informational purposes. It does not constitute medical advice or a professional recommendation. Usage Responsibility: Elumia Med Store is a marketplace. The responsibility for the correct clinical use, installation, and maintenance of any medical device lies solely with the buyer and their qualified medical staff.",
      },
      {
        q: "4. Marketplace Model & Product Accuracy",
        a: "Intermediary Role: Elumia Med Store onboards third-party suppliers of medical equipment and drugs. While we vet suppliers for valid UAE trade licenses, the warranty for the product is provided by the original manufacturer or supplier. Technical Specs: We strive for accuracy, but we do not warrant that product descriptions are error-free. Buyers are encouraged to download the technical data sheets provided for DHA inspection readiness.",
      },
      {
        q: "5. Pricing and Payments",
        a: "Pricing: Final checkout totals are calculated from the product subtotal after discounts or offers, plus any applicable delivery charge. Payment Gateways: Online payments are processed via Stripe. Installment plans are provided by Tabby and Tamara (via Nomod). By using these, you agree to their respective terms. Bulk Orders: For bulk or offline orders, payments must be made to our Mashreq Bank account as specified on the pro forma invoice.",
      },
      {
        q: "6. Shipping & Temperature Control (EMX)",
        a: "Logistics: Deliveries are fulfilled by EMX (Emirates Post). Cold Chain: For temperature-sensitive medical products, we ensure compliant packaging at the point of dispatch. The buyer must ensure a representative is available to receive and correctly store the items immediately upon delivery. Inspection: Upon delivery, the buyer must inspect the goods and report any damage or discrepancies within 24 hours.",
      },
      {
        q: "7. Returns and Refunds",
        a: "Non-Returnable Items: Due to health and safety regulations, the following items cannot be returned: Sterile medical consumables (syringes, needles, etc.) if the seal is broken. Pharmaceutical products/drugs. Custom-ordered medical devices. Defective Goods: If a product is defective, it will be repaired, replaced, or refunded in accordance with the UAE Consumer Protection Law (Federal Law No. 15 of 2020).",
      },
      {
        q: "8. Limitation of Liability",
        a: "To the maximum extent permitted by UAE law, Elumia Med Store shall not be liable for: Medical malpractice or injury resulting from the use of products purchased on the platform. Indirect, incidental, or consequential damages. Delays in delivery caused by regulatory inspections or customs.",
      },
      {
        q: "9. Governing Law",
        a: "These terms are governed by the federal laws of the United Arab Emirates and the local laws of the Emirate of Dubai. Any disputes shall be subject to the exclusive jurisdiction of the Dubai Courts.",
      },
      {
        q: "10. Contact Us",
        a: "For questions regarding these terms, please contact: Email: info@elumiamedstore.com",
      },
    ],
  },

  privacy: {
    title: "Privacy Policy",
    content: [
      "PRIVACY POLICY",
      "Effective Date: 01-05-2026",
      'Elumia Med Store ("we," "us," or "our") is committed to protecting the privacy and security of your personal and professional data.',

      {
        q: "1. Data We Collect",
        a: "As a medical marketplace, we collect data necessary to facilitate B2B and B2C transactions: Identity Data: Name, Emirates ID (for verification), professional medical license details (DHA/MOHAP), and trade license for clinic accounts. Contact Data: Billing address, clinic/delivery address, email, and phone number. Financial Data: We do not store full credit card details on our servers. All payments are processed via Stripe or Nomod. We only receive transaction confirmations and partial card info (last 4 digits). Technical Data: IP address, browser type, and usage patterns on elumiamedstore.com.",
      },
      {
        q: "2. How We Use Your Data",
        a: 'We process your information based on the following legal grounds: Contractual Necessity: To process orders, manage deliveries via EMX, and handle payments. Legal Obligation: To verify medical licenses before selling regulated drugs or equipment. Legitimate Interest: To improve our marketplace offerings and notify clinics about new "Aesthetic" or "Emergency" equipment arrivals.',
      },
      {
        q: "3. Data Sharing with Third Parties",
        a: 'We only share your data with essential partners to complete your service: Logistics Partner (EMX): We share your delivery address and contact person details for shipment fulfillment. Payment Providers (Stripe/Nomod): Your payment data is shared directly with these providers to facilitate secure checkout and "Buy Now, Pay Later" installments (Tabby/Tamara). Regulatory Authorities: We may share data with MOHAP or DHA if required for compliance audits regarding medical supply chains.',
      },
      {
        q: "4. Data Retention",
        a: "In accordance with UAE commercial and health laws: Transaction Records: Kept for at least 5 years to comply with audit and record-keeping regulations. Medical License Data: Kept as long as your account is active to ensure continued eligibility for professional-grade purchases.",
      },
      {
        q: "5. Your Rights under UAE Law",
        a: "Under Federal Decree-Law No. 45 of 2021, you have the right to: Access: Request a copy of the personal data we hold about you. Correction: Request that we update any inaccurate clinic or personal information. Erasure: Request the deletion of your data (subject to legal retention requirements for medical sales). Restriction: Request that we stop using your data for marketing (e.g., newsletters).",
      },
      {
        q: "6. Security Measures",
        a: "We implement industry-standard encryption and security protocols (SSL/TLS) to protect data in transit. Access to sensitive clinic data is restricted to authorized Elumia personnel only.",
      },
      {
        q: "7. Contact Information",
        a: "For any privacy-related inquiries or to exercise your data rights, please contact: Data Protection Officer Elumia Med Store Email: info@elumiamedstore.com",
      },
    ],
  },

  Questions: {
    title: "Frequently Asked Questions",
    content: [
      {
        q: "Is Elumia Med store licensed to sell medical products in the UAE?",
        a: "Yes. Elumia Med store operates in full compliance with UAE health regulations. We only onboard suppliers and products that meet MOHAP (Ministry of Health and Prevention) and DHA (Dubai Health Authority) standards.",
      },
      {
        q: "Are your products authentic and certified?",
        a: "Absolutely. We deal directly with authorized distributors. All medical devices and consumables carry necessary certifications such as CE, FDA, or ISO, which are required for clinic licensing inspections in Dubai.",
      },
      {
        q: "Do you offer bulk discounts for new clinic setups?",
        a: 'Yes, we specialize in helping new aesthetic and general clinics in the UAE. For large-scale procurement or "Clinic Launch Kits," please click the "Request a Quote" button on the product page or contact our Sales team directly.',
      },
      {
        q: "Can I see the technical specifications for DHA inspections?",
        a: "Yes. For critical equipment like Emergency Crash Carts and medical devices, technical data sheets are available for download on the product page to assist with your facility's licensing.",
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit/debit cards via Stripe. For offline bulk orders, we accept Bank Transfers (Mashreq Bank) and Cheques.",
      },
      {
        q: 'Do you offer "Buy Now, Pay Later" options?',
        a: "Yes! We have partnered with Tabby and Tamara (via Nomod) to allow clinics to split their payments into 4 interest-free installments. This is ideal for managing cash flow during your clinic's opening phase.",
      },
      {
        q: "How long does delivery take within Dubai and the UAE?",
        a: "Through our partnership with EMX (Emirates Post), we offer standard delivery within 24–48 hours for stocked items.",
      },
      {
        q: "Do you handle temperature-sensitive medical supplies?",
        a: "Yes. We ensure that all medical drugs and sensitive consumables are handled through climate-controlled logistics to maintain product integrity from our warehouse to your clinic.",
      },
      {
        q: "What is your return policy for medical supplies?",
        a: "Due to the nature of medical products, returns are only accepted for damaged or incorrect items reported within 24 hours of delivery, provided the sterile packaging remains unopened.",
      },
      {
        q: "Do you provide the necessary documentation for DHA facility inspections?",
        a: "Yes. For all relevant medical devices and equipment (like Emergency Crash Carts), we provide technical data sheets, CE/FDA certificates, and user manuals required by the Dubai Health Authority (DHA) for your facility's final inspection.",
      },
      {
        q: "Are your aesthetic consumables registered with MOHAP?",
        a: "Every product on our platform is sourced from authorized UAE distributors. We ensure that medical-grade disposables, medicines, and supplements are registered with the Ministry of Health and Prevention (MOHAP) and comply with local labeling laws.",
      },
      {
        q: 'Can you help us with the "Equipment List" for a new clinic license?',
        a: "Absolutely. Our Business Development team can assist you in selecting a compliant bundle of emergency devices and consumables based on your specific clinic type (Aesthetic, General, or Dental) to ensure you meet regulatory requirements.",
      },
      {
        q: "How do you ensure the quality of temperature-sensitive products?",
        a: "We understand the sensitivity of aesthetic products and medicines. Our logistics partner, EMX, utilizes climate-controlled vehicles, and our storage follows strict GWP (Good Warehousing Practice) to ensure products reach you in optimal condition.",
      },
      {
        q: "What is the shelf life/expiry of the consumables I order?",
        a: 'We strictly monitor batch expiries. We guarantee that all disposables and medicines delivered will have a minimum shelf life of 6–12 months unless clearly stated otherwise in a "Clearance" section.',
      },
      {
        q: "Can I pay via Bank Transfer or Cheque for bulk orders?",
        a: "Yes. While our online portal uses Stripe, we accept offline payments via Mashreq Bank for bulk B2B orders. Please contact your account manager to receive a Pro-forma invoice.",
      },
      {
        q: "Do you offer credit facilities for established clinics?",
        a: "New clinics can utilize our Tabby and Tamara integration for immediate interest-free installments. For established clinics with a consistent purchase history, we can discuss credit terms (e.g., Net 30) subject to a credit review.",
      },
      {
        q: "Can I request a demo of aesthetic devices?",
        a: 'For high-value medical devices and aesthetic equipment, we can arrange a physical or virtual demo at your facility or our partner showrooms. Please contact us via the "Request a Demo" button on the product page.',
      },
      {
        q: "What happens if a medical device fails?",
        a: "All medical equipment sold by Elumia Med store comes with a standard Manufacturer's Warranty (typically 1–2 years). We coordinate with authorized service centers in the UAE for repairs, calibration, and spare parts.",
      },
      {
        q: "Do you provide training for the equipment we buy?",
        a: 'Yes. For specialized emergency or aesthetic devices, we provide on-site user training and a "Certificate of Training" for your staff to ensure safe and compliant operation.',
      },
    ],
  },
  legal: {
    title: "Legal Disclaimer",
    content: [
      "Legal Disclaimer for Elumia Med Store",

      {
        q: "1. General Information Only",
        a: "The content provided on Elumia Med Store (https://elumiamedstore.com/), including text, graphics, images, and descriptions, is for informational purposes only. It is not intended to be a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of a qualified healthcare provider with any questions regarding a medical condition.",
      },
      {
        q: "2. Professional Use & Licensing",
        a: "Elumia Med Store supplies medical drugs, devices, and consumables intended for use by licensed healthcare professionals and facilities. Responsibility: By purchasing professional-grade equipment (e.g., Emergency Crash Carts, Medical Devices), the buyer confirms they possess the necessary DHA/MOHAP licenses to operate and utilize such equipment. Misuse: Elumia Med Store is not liable for any damages, injuries, or legal consequences resulting from the unauthorized or improper use of products purchased through our platform.",
      },
      {
        q: "3. Product Accuracy & Regulatory Compliance",
        a: "While we strive to ensure all product information is accurate: Specifications: Technical data, certifications (CE, FDA, ISO), and images are provided by our onboarded suppliers. Elumia Med Store does not warrant that product descriptions are 100% error-free. MOHAP/DHA Compliance: It is the buyer's responsibility to ensure that the specific batch or model of a product meets their specific facility’s regulatory requirements in the UAE or their respective MENA territory.",
      },
      {
        q: "4. No Medical Liability",
        a: "Elumia Med Store is a marketplace and distributor. We do not practice medicine or provide medical services. We shall not be held responsible for any clinical outcomes, surgical failures, or adverse reactions resulting from the use of products sold on our site. All pharmaceutical products must be administered by licensed professionals according to UAE Law.",
      },
      {
        q: "5. Third-Party Links & Services",
        a: "Our website may contain links to third-party services, including EMX (Emirates Post) for logistics, Stripe for payments, and Nomod/Tabby/Tamara for financing. We are not responsible for the privacy practices, uptime, or terms of service of these external providers.",
      },
      {
        q: "6. Pricing & Availability",
        a: "Prices and availability are subject to change without notice. In the event of a technical error where a product is listed at an incorrect price, Elumia Med Store reserves the right to cancel orders and issue a full refund via the original payment method (Stripe/Mashreq POS).",
      },
    ],
  },
};

function FAQ() {
  const { slug } = useParams();
  const data = POLICY_CONTENT[slug];

  if (!data) {
    return (
      <div className="policy-container">
        <h2>Content Not Found</h2>
      </div>
    );
  }

  if (slug === "Questions") {
  }

  return (
    <div className="policy-page">
      {/* Header */}
      <header className="policy-header">
        <h1>{data.title}</h1>
        <p>Transparency and trust are core values at Elumia Med Store.</p>
      </header>

      {/* Content */}
      <section className="policy-content">
        {Array.isArray(data.content) &&
          data.content.map((item, index) =>
            typeof item === "string" ? (
              <p key={index}>{item}</p>
            ) : (
              <div key={index} className="faq-item">
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </div>
            ),
          )}
      </section>
    </div>
  );
}

export default FAQ;
